import React, { useState, useEffect, useRef } from 'react';
import './PostBattleScreen.css'; // Reuse existing level-up styles
import LevelUpCardPick from './shared/LevelUpCardPick';
import { effectiveMusicVolume } from '../utils/settings';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// XP formula matching server
function characterLevel(xp) {
  return Math.floor((Math.sqrt(1 + 8 * Math.floor(xp / 1000)) - 1) / 2) + 1;
}

const STAT_NAMES = {
  str: { label: 'Strength', abbr: 'STR', icon: '⚔️', desc: 'Bonus melee damage on all physical attacks', detail: 'Adds damage to every melee hit, ability strike, and physical skill. Also boosts Smithing crafting.' },
  agl: { label: 'Agility', abbr: 'AGL', icon: '🏃', desc: 'Stealing, hiding, and fleeing', detail: 'Improves Steal/Mug success, Hide checks, and Run escape chance. Does NOT affect dodge or hit chance.' },
  vit: { label: 'Vitality', abbr: 'VIT', icon: '❤️', desc: 'Boosts HP gained per level (hit die + VIT mod)', detail: 'Higher VIT increases the HP you gain each level. VIT modifier = floor((VIT - 10) / 2). Also used for Mind Over Matter (HP→MP conversion).' },
  men: { label: 'Mentality', abbr: 'MEN', icon: '🔮', desc: 'MP per level · spell damage & penetration', detail: 'Higher MEN increases MP gained per level and scales ALL spell damage, heal power, and spell resistance penetration. Also boosts Alchemy crafting.' },
  cha: { label: 'Charisma', abbr: 'CHA', icon: '✨', desc: 'Pet charm & shop discounts', detail: 'Improves Charm success rate vs monsters. Gives shop buy/sell discounts (modifier × 1.5%). Primary stat for Druids and pet builds.' },
};

/**
 * LevelUpModal — Standalone modal for allocating pending stat points
 * AND picking a deck-building card reward.
 */
const LevelUpModal = ({ character, onCharacterUpdate, onClose }) => {
  const [statAllocation, setStatAllocation] = useState({ str: 0, agl: 0, vit: 0, men: 0, cha: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Card pick state
  const [cardChoices, setCardChoices] = useState(null); // array of 3 cards or null while loading
  const [selectedCard, setSelectedCard] = useState(null); // key of selected card
  const [cardPickPhase, setCardPickPhase] = useState(false); // true = show card picker after stats
  const [forgeTokens, setForgeTokens] = useState(0); // current token count for display
  // v737.1: play level-up music while modal is open
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio('/music/levelup.mp3');
    audio.loop = true;
    audio.volume = effectiveMusicVolume();
    audioRef.current = audio;
    audio.play().catch(() => { /* autoplay blocked — silent */ });
    return () => {
      try { audio.pause(); audio.currentTime = 0; } catch { /* ignore */ }
    };
  }, []);

  const currentLevel = character.level;
  const trueLevel = characterLevel(character.experience);
  const levelsGained = trueLevel - currentLevel;

  const totalAllocated = Object.values(statAllocation).reduce((s, v) => s + v, 0);
  const pointsRemaining = levelsGained - totalAllocated;

  // Fetch card choices on mount
  useEffect(() => {
    const fetchCardChoices = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/player/levelup/card-choices`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setCardChoices(data.choices || []);
          setForgeTokens(data.forgeTokens || 0);
        }
      } catch (e) {
        console.warn('Could not fetch card choices:', e);
        setCardChoices([]);
      }
    };
    fetchCardChoices();
  }, []);

  const handleStatIncrease = (stat) => {
    if (pointsRemaining <= 0) return;
    setStatAllocation(prev => ({ ...prev, [stat]: prev[stat] + 1 }));
  };

  const handleStatDecrease = (stat) => {
    if (statAllocation[stat] <= 0) return;
    setStatAllocation(prev => ({ ...prev, [stat]: prev[stat] - 1 }));
  };

  const handleProceedToCards = () => {
    if (pointsRemaining !== 0) return;
    if (cardChoices && cardChoices.length > 0) {
      setCardPickPhase(true);
    } else {
      // No cards available — submit directly
      handleConfirm(null);
    }
  };

  const handleConfirm = async (cardPick) => {
    if (pointsRemaining !== 0) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/player/levelup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          statIncrease: statAllocation,
          cardPick: cardPick || null,
        })
      });

      const data = await response.json();
      if (response.ok) {
        await onCharacterUpdate();
        onClose();
      } else {
        setError(data.error || 'Failed to level up');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Network error — try again');
      setIsSubmitting(false);
    }
  };

  // Calculate skill points gained
  const skillPtsOld = currentLevel < 5 ? 0 : Math.floor((currentLevel - 5) / 3) + 1;
  const skillPtsNew = trueLevel < 5 ? 0 : Math.floor((trueLevel - 5) / 3) + 1;
  const skillPtsGained = skillPtsNew - skillPtsOld;

  // ── Card Pick Phase ──
  if (cardPickPhase) {
    return (
      <div className="post-battle-overlay">
        <div className="post-battle-panel levelup" style={{ maxWidth: '700px' }}>
          <LevelUpCardPick
            cardChoices={cardChoices || []}
            forgeTokens={forgeTokens}
            selectedCard={selectedCard}
            onSelect={setSelectedCard}
            onConfirm={() => handleConfirm(selectedCard)}
            onSkip={() => handleConfirm(null)}
            onBack={() => { setCardPickPhase(false); setSelectedCard(null); }}
            isSubmitting={isSubmitting}
            error={error}
            level={trueLevel}
          />
        </div>
      </div>
    );
  }

  // ── Stat Allocation Phase ──
  return (
    <div className="post-battle-overlay">
      <div className="post-battle-panel levelup">
        <div className="pb-header levelup-header">
          <div className="pb-icon">⬆️</div>
          <h2>Pending Level Up!</h2>
          <div className="level-badge">Level {currentLevel} → Level {trueLevel}</div>
        </div>

        <div className="pb-pending-notice" style={{
          background: '#2a2a1a',
          border: '1px solid #c0a050',
          borderRadius: '8px',
          padding: '10px 14px',
          margin: '0 0 12px 0',
          color: '#e0d0a0',
          fontSize: '0.85em',
          textAlign: 'center'
        }}>
          ⚠️ You have <strong>{levelsGained} unspent stat point{levelsGained !== 1 ? 's' : ''}</strong> from a previous level up. Please allocate them to continue.
        </div>

        {/* Stat allocation */}
        <div className="pb-stat-allocation">
          <h3>Allocate Stat Points</h3>
          <div className="stat-points-remaining">
            Points remaining: <strong>{pointsRemaining}</strong> / {levelsGained}
          </div>

          {skillPtsGained > 0 && (
            <div className="ability-point-notice" style={{ borderColor: '#c0a050', background: '#2a2a1a' }}>
              🌳 You also earned <strong>{skillPtsGained} skill point{skillPtsGained > 1 ? 's' : ''}</strong>! Open the Skill Tree (T) after confirming.
            </div>
          )}

          <div className="stat-rows">
            {Object.entries(STAT_NAMES).map(([key, stat]) => {
              const currentVal = character?.[
                key === 'str' ? 'strength' :
                key === 'agl' ? 'agility' :
                key === 'vit' ? 'vitality' :
                key === 'men' ? 'mentality' : 'charisma'
              ] || 0;

              const bonusNote = null;

              return (
                <div key={key} className="stat-alloc-wrapper">
                  <div className="stat-alloc-row">
                    <span className="stat-icon">{stat.icon}</span>
                    <span className="stat-name">{stat.label}</span>
                    <span className="stat-current">{currentVal}</span>

                    <button
                      className="stat-btn minus"
                      onClick={() => handleStatDecrease(key)}
                      disabled={statAllocation[key] <= 0}
                    >−</button>
                    <span className={`stat-added ${statAllocation[key] > 0 ? 'has-points' : ''}`}>
                      +{statAllocation[key]}
                    </span>
                    <button
                      className="stat-btn plus"
                      onClick={() => handleStatIncrease(key)}
                      disabled={pointsRemaining <= 0}
                    >+</button>

                    <span className="stat-new">{currentVal + statAllocation[key]}</span>
                    {bonusNote && <span className="stat-bonus-note">{bonusNote}</span>}
                  </div>
                  <div className="stat-desc">{stat.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card choice preview */}
        {cardChoices && cardChoices.length > 0 && (
          <div style={{
            background: 'rgba(30, 30, 50, 0.5)',
            border: '1px solid rgba(155, 89, 182, 0.3)',
            borderRadius: '8px',
            padding: '8px 12px',
            margin: '8px 0',
            textAlign: 'center',
            fontSize: '0.82rem',
            color: '#c8a2e8'
          }}>
            🃏 <strong>{cardChoices.length} card reward{cardChoices.length !== 1 ? 's' : ''}</strong> available after stat allocation!
          </div>
        )}

        {error && <div className="pb-error">{error}</div>}

        <button
          className="pb-confirm-btn"
          onClick={handleProceedToCards}
          disabled={pointsRemaining !== 0 || isSubmitting}
        >
          {isSubmitting
            ? 'Leveling up...'
            : cardChoices && cardChoices.length > 0
              ? `Next: Choose Card Reward →`
              : `Confirm Level ${trueLevel}`}
        </button>
      </div>
    </div>
  );
};

export default LevelUpModal;
